export interface ApiRoute {
  path: string
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  nextApiRoute?: boolean
  withAuth?: boolean
  returnFullResponse?: boolean
  additionalRequestOptions?: RequestInit
}

export const apiRoutes = {
  authentication: {
    sendSetPasswordEmail: {
      path: '/authentication/send-set-password-email',
      method: 'POST',
    },
  },
  contactEngagement: {
    issues: {
      path: '/contact-engagement/:id/issues',
      method: 'GET',
    },
    activities: {
      path: '/contact-engagement/:id/activities',
      method: 'GET',
    },
    priorityAnswers: {
      path: '/contact-engagement/:id/priority-answers',
      method: 'GET',
    },
  },
  contacts: {
    list: {
      path: '/contacts',
      method: 'GET',
    },
    get: {
      path: '/contacts/:id',
      method: 'GET',
    },
    download: {
      path: '/contacts/download',
      method: 'GET',
    },
  },
  polls: {
    list: {
      path: '/polls',
      method: 'GET',
    },
    get: {
      path: '/polls/:pollId',
      method: 'GET',
    },
    topIssues: {
      path: '/polls/:pollId/top-issues',
      method: 'GET',
    },
    initialPoll: {
      path: '/polls/initial-poll',
      method: 'POST',
    },
    imageUploadUrl: {
      path: '/polls/image-upload-url',
      method: 'POST',
    },
    analyzeBias: {
      path: '/polls/analyze-bias',
      method: 'POST',
    },
    hasPolls: {
      path: '/polls/has-polls',
      method: 'GET',
    },
    downloadResponses: {
      path: '/polls/:pollId/download-responses',
      method: 'GET',
    },
  },
  electedOffice: {
    current: {
      path: '/elected-office/current',
      method: 'GET',
    },
    mine: {
      path: '/elected-office/mine',
      method: 'GET',
    },
  },
  homepage: {
    subscribeEmail: {
      path: '/subscribe',
      method: 'POST',
    },
    declarationSignatures: {
      list: {
        path: '/declare/list',
        method: 'GET',
      },
    },
  },
  user: {
    updateUser: {
      path: '/users/me',
      method: 'PUT',
    },
    updateMeta: {
      path: '/users/me/metadata',
      method: 'PUT',
    },
    getMeta: {
      path: '/users/me/metadata',
      method: 'GET',
    },
    getUser: {
      path: '/users/me',
      method: 'GET',
    },
    deleteAccount: {
      path: '/users/:id',
      method: 'DELETE',
    },
    uploadAvatar: {
      path: '/users/me/upload-image',
      method: 'POST',
    },
    files: {
      generateSignedUploadUrl: {
        path: '/users/files/generate-signed-upload-url',
        method: 'PUT',
      },
    },
  },
  campaign: {
    create: {
      path: '/campaigns',
      method: 'POST',
    },
    update: {
      path: '/campaigns/mine',
      method: 'PUT',
    },
    get: {
      path: '/campaigns/mine',
      method: 'GET',
    },
    status: {
      path: '/campaigns/mine/status',
      method: 'GET',
    },
    findBySlug: {
      path: '/campaigns/slug/:slug',
      method: 'GET',
    },
    list: {
      path: '/campaigns',
      method: 'GET',
    },
    launch: {
      path: '/campaigns/launch',
      method: 'POST',
    },
    district: {
      path: '/campaigns/mine/district',
      method: 'PUT',
    },
    updateHistory: {
      create: {
        path: '/campaigns/mine/update-history',
        method: 'POST',
      },
      list: {
        path: '/campaigns/mine/update-history',
        method: 'GET',
      },
      delete: {
        path: '/campaigns/mine/update-history/:id',
        method: 'DELETE',
      },
    },
    campaignPosition: {
      create: {
        path: '/campaigns/:id/positions',
        method: 'POST',
      },
      update: {
        path: '/campaigns/:id/positions/:positionId',
        method: 'PUT',
      },
      delete: {
        path: '/campaigns/:id/positions/:positionId',
        method: 'DELETE',
      },
      find: {
        path: '/campaigns/:id/positions',
        method: 'GET',
      },
    },
    planVersion: {
      path: '/campaigns/mine/plan-version',
      method: 'GET',
    },
    tasks: {
      list: {
        path: '/campaigns/tasks',
        method: 'GET',
      },
      generate: {
        path: '/campaigns/tasks/generate',
        method: 'POST',
      },
      generateStream: {
        path: '/campaigns/tasks/generate/stream',
        method: 'GET',
      },
      complete: {
        path: '/campaigns/tasks/complete/:taskId',
        method: 'PUT',
      },
      revert: {
        path: '/campaigns/tasks/complete/:taskId',
        method: 'DELETE',
      },
      delete: {
        path: '/campaigns/tasks/:taskId',
        method: 'DELETE',
      },
      deleteAll: {
        path: '/campaigns/tasks',
        method: 'DELETE',
      },
    },
    tcrCompliance: {
      fetch: {
        path: '/campaigns/tcr-compliance/mine',
        method: 'GET',
      },
      complianceState: {
        path: '/campaigns/tcr-compliance/mine/compliance-state',
        method: 'GET',
      },
      create: {
        path: '/campaigns/tcr-compliance',
        method: 'POST',
      },
      createAgentic: {
        path: '/campaigns/tcr-compliance/agentic',
        method: 'POST',
      },
      submitCvPin: {
        path: '/campaigns/tcr-compliance/:tcrComplianceId/submit-cv-pin',
        method: 'POST',
      },
    },
  },
  topIssue: {
    create: {
      path: '/top-issues',
      method: 'POST',
    },
    update: {
      path: '/top-issues/:id',
      method: 'PUT',
    },
    delete: {
      path: '/top-issues/:id',
      method: 'DELETE',
    },
    list: {
      path: '/top-issues',
      method: 'GET',
    },
    byLocation: {
      path: '/top-issues/by-location',
      method: 'GET',
    },
    position: {
      create: {
        path: '/positions',
        method: 'POST',
      },
      update: {
        path: '/positions/:id',
        method: 'PUT',
      },
      delete: {
        path: '/positions/:id',
        method: 'DELETE',
      },
    },
  },
  voters: {
    voterFile: {
      get: {
        path: '/voters/voter-file',
        method: 'GET',
      },
    },
  },
  voterFileFilter: {
    create: {
      path: '/voters/voter-file/filter',
      method: 'POST',
    },
    list: {
      path: '/voters/voter-file/filters',
      method: 'GET',
    },
    update: {
      path: '/voters/voter-file/filter/:id',
      method: 'PUT',
    },
    delete: {
      path: '/voters/voter-file/filter/:id',
      method: 'DELETE',
    },
  },
  admin: {
    bustCache: {
      path: '/revalidate',
      method: 'POST',
      nextApiRoute: true,
    },
    user: {
      get: {
        path: '/admin/users/:id',
        method: 'GET',
      },
      list: {
        path: '/admin/users',
        method: 'GET',
      },
      create: {
        path: '/admin/users',
        method: 'POST',
      },
      delete: {
        path: '/admin/users/:id',
        method: 'DELETE',
      },
      impersonate: {
        path: '/admin/users/impersonate',
        method: 'POST',
      },
    },
    campaign: {
      update: {
        path: '/admin/campaigns/:id',
        method: 'PUT',
      },
      delete: {
        path: '/admin/campaigns/:id',
        method: 'DELETE',
      },
      proNoVoterFile: {
        path: '/admin/campaigns/pro-no-voter-file',
        method: 'GET',
      },
    },
  },
  elections: {
    racesByYear: {
      path: '/elections/races-by-year',
      method: 'GET',
    },
    raceByPosition: {
      path: '/elections/race-by-position',
      method: 'GET',
    },
    districts: {
      types: {
        path: '/elections/districts/types',
        method: 'GET',
      },
      names: {
        path: '/elections/districts/names',
        method: 'GET',
      },
    },
  },
  payments: {
    createCheckoutSession: {
      path: '/payments/purchase/checkout-session',
      method: 'POST',
    },
    createPortalSession: {
      path: '/payments/purchase/portal-session',
      method: 'POST',
    },
    createCustomCheckoutSession: {
      path: '/payments/purchase/create-checkout-session',
      method: 'POST',
    },
    completeCheckoutSession: {
      path: '/payments/purchase/complete-checkout-session',
      method: 'POST',
    },
    completeFreePurchase: {
      path: '/payments/purchase/complete-free-purchase',
      method: 'POST',
    },
  },
  logError: {
    path: '/error-logger',
    method: 'POST',
  },
  textMessaging: {
    list: {
      path: '/text-campaigns',
      method: 'GET',
    },
  },
  outreach: {
    create: {
      path: '/outreach',
      method: 'POST',
    },
    drafts: {
      path: '/outreach/drafts',
      method: 'POST',
    },
    list: {
      path: '/outreach',
      method: 'GET',
    },
  },
  p2p: {
    createPhoneList: {
      path: '/p2p/phone-list',
      method: 'POST',
    },
    phoneListBuildStatus: {
      path: '/p2p/phone-list/build/:buildId/status',
      method: 'GET',
    },
  },
  website: {
    get: {
      path: '/websites/mine',
      method: 'GET',
    },
    create: {
      path: '/websites',
      method: 'POST',
    },
    update: {
      path: '/websites/mine',
      method: 'PUT',
    },
    preview: {
      path: '/websites/:vanityPath/preview',
      method: 'GET',
    },
    getContacts: {
      path: '/websites/mine/contacts',
      method: 'GET',
    },
    getViews: {
      path: '/websites/mine/views',
      method: 'GET',
    },
    submitContactForm: {
      path: '/websites/:vanityPath/contact-form',
      method: 'POST',
    },
    validateVanityPath: {
      path: '/websites/validate-vanity-path',
      method: 'POST',
    },
    trackView: {
      path: '/websites/:vanityPath/track-view',
      method: 'POST',
    },
  },
  domain: {
    search: {
      path: '/domains/search',
      method: 'GET',
    },
    register: {
      path: '/domains',
      method: 'POST',
    },
    completeRegistration: {
      path: '/domains/complete',
      method: 'POST',
    },
    delete: {
      path: '/domains/:id',
      method: 'DELETE',
    },
    status: {
      path: '/domains/status',
      method: 'GET',
    },
  },
} as const
